import {Component, inject, OnInit} from '@angular/core';
import {
  AlertComponent,
  ButtonComponent,
  FormButtonsComponent,
  InaccessibleDirective,
  LoadingIndicatorService
} from '@icz/angular-essentials';
import {
  FormFieldComponent,
  IczFormContainerDirective,
  IczFormControl,
  IczFormGroup,
  IczValidators
} from '@icz/angular-form-elements';
import {injectModalData, injectModalRef} from '@icz/angular-modal';
import {TranslatePipe, TranslateService} from '@ngx-translate/core';
import {EsslErrorCodeExceptionDto, InternalNotificationKey} from '|api/commons';
import {ApiDocumentService, ApiFileService, DeactivationDto, PrepareDeactivationDto} from '|api/document';
import {ReactiveFormsModule} from '@angular/forms';
import {AsyncPipe} from '@angular/common';
import {
  CheckUnsavedFormDialogService,
  IczFormSubmitDirective,
  IFormGroupCheckable,
  InterpolateBackendErrorPipe
} from '@icz/angular-application-core';
import {DocumentToastService, DocumentToastType, FileToastService, FileToastType} from '|ui-essl-core';

export interface DocumentFileDeativationDialogData {
  entityId: number;
  subject: string;
  isDocument: boolean;
}

@Component({
  selector: 'icz-document-file-deativation-dialog',
  templateUrl: './document-file-deactivation-dialog.component.html',
  styleUrls: ['./document-file-deactivation-dialog.component.scss'],
  providers: [
    CheckUnsavedFormDialogService
  ],
  imports: [ReactiveFormsModule, IczFormContainerDirective, InaccessibleDirective, AlertComponent, FormFieldComponent, FormButtonsComponent, ButtonComponent, IczFormSubmitDirective, AsyncPipe, InterpolateBackendErrorPipe, TranslatePipe]
})
export class DocumentFileDeactivationDialogComponent implements OnInit, IFormGroupCheckable {

  protected loadingService = inject(LoadingIndicatorService);
  protected modalRef = injectModalRef<Nullable<boolean>>();
  protected checkUnsavedService = inject(CheckUnsavedFormDialogService);
  private translateService = inject(TranslateService);
  private apiDocumentService = inject(ApiDocumentService);
  private apiFileService = inject(ApiFileService);
  private documentToastService = inject(DocumentToastService);
  private fileToastService = inject(FileToastService);
  protected data = injectModalData<DocumentFileDeativationDialogData>();

  formGroup = new IczFormGroup({
    reason: new IczFormControl<Nullable<string>>(null, [IczValidators.required()]),
  });

  formGroupsToCheck!: string[];

  isDeativationValid = false;
  isValidationDone = false;

  validationErrors: EsslErrorCodeExceptionDto[] = [];

  headingContext!: {entityTypeToHeading_1: string, entityTypeToHeading_2: string};

  ngOnInit() {
    const validationReq$ = this.data.isDocument ? this.apiDocumentService.documentPrepareDeactivation({id: this.data.entityId}) : this.apiFileService.filePrepareDeactivation({id: this.data.entityId});

    this.loadingService.doLoading(validationReq$, this).subscribe((validationResult: PrepareDeactivationDto) => {
      this.isDeativationValid = !isNil(validationResult.valid) ? validationResult.valid : true;
      this.validationErrors = !isNil(validationResult.errors) ? validationResult.errors : [];
      this.isValidationDone = true;
    });

    this.checkUnsavedService.addUnsavedFormCheck(this, ['formGroup']);

    if (this.data.isDocument) {
      this.headingContext = {
        entityTypeToHeading_1: this.translateService.instant('dokumentu'),
        entityTypeToHeading_2: this.translateService.instant('dokument'),
      };
    } else {
      this.headingContext = {
        entityTypeToHeading_1: this.translateService.instant('spisu'),
        entityTypeToHeading_2: this.translateService.instant('spis'),
      };
    }
  }

  submit() {
    const requestDto: DeactivationDto = {
      deactivationReason: this.formGroup.get('reason')!.value,
    };

    const deactivationReq$ = this.data.isDocument ?
      this.apiDocumentService.documentDeactivateDocument({id: this.data.entityId, body: requestDto}) :
      this.apiFileService.fileDeactivateFile({id: this.data.entityId, body: requestDto});

    this.loadingService.doLoading(
      deactivationReq$,
      this,
    ).subscribe(_ => {
      if (this.data.isDocument) {
        this.documentToastService.dispatchDocumentInfoToast(DocumentToastType.DOCUMENT_DEACTIVATED, {
          [InternalNotificationKey.DOCUMENT_ID]: this.data.entityId,
          [InternalNotificationKey.DOCUMENT_SUBJECT]: this.data.subject,
        });
      } else {
        this.fileToastService.dispatchFileInfoToast(FileToastType.FILE_DEACTIVATED, {
          [InternalNotificationKey.FILE_ID]: this.data.entityId,
          [InternalNotificationKey.FILE_SUBJECT]: this.data.subject,
        });
      }
      this.modalRef.close(true);
    });
  }

  cancel() {
    this.modalRef.close(null);
  }
}
